/**
 * Per-splat edit state bitmask, one byte per splat.
 *
 * `hidden` is SuperSplat's "locked": the splat stays in the saved file but is
 * drawn dimmed and ignored by every selection tool until it is unhidden.
 * `deleted` splats are not drawn and are dropped on save.
 */
export const SplatState = {
    selected: 1,
    hidden: 2,
    deleted: 4,
    /**
     * Display-only preview ("about to be removed"), outside undo history. Set
     * through SplatEditModel.setMarked; edit ops keep it, the system clears it
     * before any edit runs.
     */
    marked: 8,
    /**
     * Display-only, outside undo history: not drawn while a clone stamp stroke
     * paints over it in replace mode (the commit then deletes it). Set through
     * SplatEditModel.hidePreview; the system clears it before any edit runs.
     */
    previewHidden: 16,
} as const;

/** Splats with either bit set can't be selected. */
export const UNSELECTABLE = SplatState.hidden | SplatState.deleted;

export type SelectionOp = 'set' | 'add' | 'remove' | 'intersect';

/**
 * Apply a selection hit to one state byte. Hidden and deleted splats never
 * change. Returns the new byte.
 */
export function applySelectionBit(state: number, hit: boolean, op: SelectionOp): number {
    if (state & UNSELECTABLE) return state;
    switch (op) {
        case 'set':
            return hit ? state | SplatState.selected : state & ~SplatState.selected;
        case 'add':
            return hit ? state | SplatState.selected : state;
        case 'remove':
            return hit ? state & ~SplatState.selected : state;
        case 'intersect':
            return hit ? state : state & ~SplatState.selected;
        default:
            return state;
    }
}

/** Map pointer-event modifiers to a selection op: Shift+Ctrl intersect, Shift add, Ctrl/Alt remove. */
export function selectionOpFromModifiers(
    e: { shiftKey: boolean; ctrlKey: boolean; metaKey?: boolean; altKey?: boolean },
    fallback: SelectionOp = 'set',
): SelectionOp {
    const ctrl = e.ctrlKey || !!e.metaKey;
    if (e.shiftKey && (ctrl || e.altKey)) return 'intersect';
    if (e.shiftKey) return 'add';
    if (ctrl || e.altKey) return 'remove';
    return fallback;
}
